import React, { useState, useEffect } from "react";
import {
  addAppRegistration,
  AppRegistration,
  AppRegistrationModel,
  updateAppRegistration,
} from "@/api/appRegistrations";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Customer } from "@/api/customers";
import { Grid } from "fluentui-react-grid";
import TextInput from "@/ui-controls/TextInput";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import {
  Dropdown,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  Overlay,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import { Application } from "@/api/applications";

interface Props {
  onSave?: (newAppReg: AppRegistration) => any;
  onUpdate?: () => any;
  onClose?: () => any;
  appReg?: AppRegistration;
  customer: Customer;
  applications: Application[];
}

const NewCustomerAppRegistrationForm = (props: Props) => {
  const [isEditing] = useState<boolean>(!!props.appReg);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Auto-clear error message after 8 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const clearMessages = () => {
    setErrorMessage(null);
  };

  const handleError = (error: any) => {
    if (error?.errors) {
      const fieldErrors = Object.values(error.errors) as string[][];
      if (fieldErrors.length && fieldErrors[0].length) {
        setErrorMessage(fieldErrors[0][0]);
        return;
      }
    }
    setErrorMessage("An unexpected error occurred. Please try again.");
    console.error(error);
  };

  const saveAppRegistration = (model: AppRegistrationModel) => {
    setErrorMessage(null);
    setIsSaving(true);

    const action =
      isEditing && props.appReg
        ? updateAppRegistration(props.appReg.Id, model)
        : addAppRegistration(model);

    action
      .then((result) => {
        setIsSaving(false);
        if (isEditing) props.onUpdate?.();
        else props.onSave?.(result);
      })
      .catch((error) => {
        setIsSaving(false);
        handleError(error);
      });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      clientName: props.appReg?.ClientName || "",
      clientDescription: props.appReg?.ClientDescription || "",
      contactEmail: props.appReg?.ContactEmail || "",
      applicationIds:
        props.appReg?.ApplicationIds ||
        (props.applications.map((a) => a.Id) as number[]),
    },
    validationSchema: Yup.object({
      clientName: Yup.string().required("Client Name is required").max(250),
      contactEmail: Yup.string()
        .required("Contact email is required")
        .max(250)
        .email("Must be a valid email address"),
      applicationIds: Yup.array()
        .of(Yup.number())
        .min(1, "Select at least one app"),
    }),
    onSubmit: (values) => {
      const model: AppRegistrationModel = {
        ClientName: values.clientName,
        ClientDescription: values.clientDescription,
        ContactEmail: values.contactEmail,
        CustomerId: props.customer.Id,
        ApplicationIds: values.applicationIds,
      };
      saveAppRegistration(model);
    },
  });

  const appOptions: IDropdownOption[] = props.applications.map((app) => ({
    key: app.Id,
    text: app.Name,
  }));

  return (
    <>
      {/* full-screen overlay when saving */}
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999, // Very high z-index to cover everything
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Spinner size={SpinnerSize.large} label="Saving registration..." />
          </div>
        </div>
      )}

      <form onSubmit={formik.handleSubmit} style={{ paddingTop: 25 }}>
        <Grid dir="ltr">
          {/* client name */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="Client Name"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("clientName"),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e);
                    clearMessages();
                  },
                }}
                hasError={
                  !!(formik.touched.clientName && formik.errors.clientName)
                }
                errorMessage={formik.errors.clientName}
              />
            </Grid.Col>
          </Grid.Row>

          {/* client description */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="Client Description"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("clientDescription"),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e);
                    clearMessages();
                  },
                }}
                hasError={
                  !!(
                    formik.touched.clientDescription &&
                    formik.errors.clientDescription
                  )
                }
                errorMessage={formik.errors.clientDescription}
              />
            </Grid.Col>
          </Grid.Row>

          {/* contact email */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="Contact Email"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("contactEmail"),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e);
                    clearMessages();
                  },
                }}
                hasError={
                  !!(formik.touched.contactEmail && formik.errors.contactEmail)
                }
                errorMessage={formik.errors.contactEmail}
              />
            </Grid.Col>
          </Grid.Row>

          {/* applications */}
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <Dropdown
                label="Applications"
                options={appOptions}
                multiSelect
                disabled={isSaving}
                selectedKeys={formik.values.applicationIds}
                onChange={(_, option) => {
                  const keys = new Set(formik.values.applicationIds);
                  if (option) {
                    if (option.selected) keys.add(option.key as number);
                    else keys.delete(option.key as number);
                    formik.setFieldValue("applicationIds", Array.from(keys));
                  }
                  clearMessages();
                }}
                errorMessage={
                  formik.touched.applicationIds && formik.errors.applicationIds
                    ? (formik.errors.applicationIds as string)
                    : undefined
                }
              />
            </Grid.Col>
          </Grid.Row>

          {/* buttons */}
          <Grid.Row className="panel-buttons">
            <Grid.Col>
              <SaveButton
                text="Save"
                baseTypeProps={{
                  type: "submit",
                  disabled: isSaving,
                }}
              />
            </Grid.Col>
            <Grid.Col>
              <CancelButton
                text="Cancel"
                onClick={() => {
                  clearMessages();
                  props.onClose?.();
                }}
                baseTypeProps={{ disabled: isSaving }}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </form>

      {/* error bar */}
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={() => setErrorMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 10000, // Above overlay
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default NewCustomerAppRegistrationForm;
